import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import type { components } from "@/shared/api";
import { cn } from "@/shared/lib";
import { BlockError } from "@/shared/ui/block-error";
import { Skeleton } from "@/shared/ui/skeleton";

import { useAssets } from "../../api/useAssets";
import { useItemTooltips } from "../../api/useItemTooltips";
import { assetName } from "../../lib/assetName";
import { AbilityGlyph } from "./AbilityGlyph";
import { formatStat } from "./formatStat";
import { statTone } from "./statTone";
import { TooltipSection } from "./TooltipSection";

type Upgrade = components["schemas"]["TooltipUpgradeOut"];

const TONE = {
  plus: cn("text-good"),
  minus: cn("text-bad"),
  plain: cn("text-foreground"),
} as const;

type AbilityInfoCardProps = {
  abilityId: number;
  renderTitle: (name: string) => ReactNode;
  closeButton?: ReactNode;
  // Строка внизу: «Сильные на Warden качают его на шагах 4, 8, 9, 10».
  footer?: ReactNode;
};

// Описание умения, как в игре при наведении на него (доска «19д»): значок, название,
// «Умение» или «Ульта», перезарядка, текст, числа и три улучшения.
export const AbilityInfoCard = ({
  abilityId,
  renderTitle,
  closeButton,
  footer,
}: AbilityInfoCardProps) => {
  const { i18n, t } = useLingui();
  const { data: assets } = useAssets();
  const tooltips = useItemTooltips(true);
  const ability = assets?.abilities.find((entry) => entry.id === abilityId);
  const tooltip = tooltips.data?.items.find((entry) => entry.id === abilityId);
  const name = ability === undefined ? t`Ability #${abilityId}` : assetName(ability.name);
  const ultimate = ability?.ultimate === true;
  const upgrades = tooltip?.upgrades ?? [];
  const cooldown =
    tooltip?.cooldown_s == null
      ? null
      : new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 1 }).format(tooltip.cooldown_s);

  return (
    <div className="flex flex-col gap-3 text-left">
      <div className="flex items-start gap-3">
        <AbilityGlyph
          name={name}
          iconUrl={ability?.icon_url ?? null}
          ultimate={ultimate}
          size={52}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {renderTitle(name)}
          <span
            className={cn("text-[13px] font-semibold", ultimate ? "text-accent" : "text-muted")}
          >
            {ultimate ? <Trans>Ultimate</Trans> : <Trans>Ability</Trans>}
          </span>
        </div>
        {closeButton}
      </div>

      {tooltips.isPending && (
        <div className="flex flex-col gap-2" aria-busy="true">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      )}
      {tooltips.isError && (
        <BlockError
          title={<Trans>Could not load the description</Trans>}
          retrying={tooltips.isFetching}
          onRetry={() => void tooltips.refetch()}
          className="px-3 py-3"
        />
      )}
      {tooltips.isSuccess && tooltip === undefined && (
        <p className="text-sm text-muted">
          <Trans>No description for this ability yet</Trans>
        </p>
      )}

      {cooldown !== null && (
        <span className="self-start rounded-full border border-border-strong px-2.5 py-0.5 text-xs text-accent-strong">
          <Trans>Cooldown {cooldown} s</Trans>
        </span>
      )}
      {tooltip?.sections.map((section, index) => (
        <TooltipSection key={`${section.kind}-${index}`} section={section} hideTitle />
      ))}
      {upgrades.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-border pt-2.5">
          <h4 className="text-xs font-semibold tracking-wider text-subtle uppercase">
            <Trans>Upgrades</Trans>
          </h4>
          <ol className="flex flex-col gap-2">
            {upgrades.map((upgrade, index) => (
              <UpgradeRow
                // Улучшения не переставляются: номер — устойчивый ключ.
                key={index}
                number={index + 1}
                upgrade={upgrade}
                locale={i18n.locale}
              />
            ))}
          </ol>
        </div>
      )}

      {footer !== undefined && (
        <p className="border-t border-border pt-2.5 text-xs text-subtle">{footer}</p>
      )}
    </div>
  );
};

const UpgradeRow = ({
  number,
  upgrade,
  locale,
}: {
  number: number;
  upgrade: Upgrade;
  locale: string;
}) => (
  <li className="flex items-start gap-2.5 text-[13px] leading-snug text-foreground-soft">
    <span
      aria-hidden="true"
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-accent font-mono text-[11px] font-semibold text-accent-foreground"
    >
      {number}
    </span>
    {upgrade.text !== "" ? (
      <span className="whitespace-pre-line">{upgrade.text}</span>
    ) : (
      <span className="flex flex-col">
        {upgrade.stats.map((stat, index) => (
          <span key={`${stat.label}-${index}`}>
            {stat.label}{" "}
            <span className={cn("font-mono font-medium", TONE[statTone(stat)])}>
              {formatStat(stat, locale)}
            </span>
          </span>
        ))}
      </span>
    )}
  </li>
);
